import { useEffect, useState } from "react";

import { InlineEditButtons } from "../../../Everou/_components/InlineEditButtons";
import { ValidatedTextInput } from "../../../Common/_components/ValidatedTextInput";
import ToolTipWrapper from "../../../Everou/_components/ToolTipWrapper";
import { Icons } from "../../../Everou/_assets";
import { RemoveAction } from "../../../Common/_components";
import { useTranslation } from "react-i18next";

// Shared building blocks for the location setting screens (Settings and Access
// link). They live at module scope so both screens render identical rows, and
// so their identity is stable: nested inside a screen they were redefined on
// every render, which remounted the inputs and killed the toggles' transition.

export function SettingsInfoTooltip({ tooltip }) {
    // The tooltip renders inside the section title, which is uppercase and
    // bold. Reset those inherited styles and allow wrapping so the text
    // reads as a normal multi-line sentence instead of one wide CAPS line.
    return (
        <ToolTipWrapper
            tooltip={
                <span
                    style={{
                        display: "block",
                        maxWidth: "24rem",
                        textTransform: "none",
                        fontWeight: 400,
                        whiteSpace: "normal",
                    }}
                >
                    {tooltip}
                </span>
            }
        >
            <img
                src={Icons.info}
                alt="info"
                className="ml-2"
                style={{ width: "20px", height: "20px", cursor: "help" }}
            />
        </ToolTipWrapper>
    );
}

export function SettingsSectionTitle({ title, tooltip = null }) {
    return (
        <div
            className="
                c-new-ft-l
                text-uppercase
                font-weight-bold
                pb-2
                d-flex
                align-items-center
            "
        >
            {title}
            {tooltip && <SettingsInfoTooltip tooltip={tooltip} />}
        </div>
    );
}

export function SettingsSubsectionTitle({ title, tooltip = null }) {
    return (
        <div className="font-weight-bold text-uppercase d-flex align-items-center">
            {title}
            {tooltip && <SettingsInfoTooltip tooltip={tooltip} />}
        </div>
    );
}

// A group of settings in a hairline card. Sections separated by nothing but
// whitespace ran together on a long screen; the border is what makes each one
// read as a block instead of one endless list.
export function SettingsSection({ title = null, tooltip = null, children }) {
    return (
        <div className="border c-rounded-big bg-white p-3 mb-3">
            {title && <SettingsSectionTitle title={title} tooltip={tooltip} />}
            {children}
        </div>
    );
}

// One setting: its name on the left (with the explanation behind an info icon,
// so a screen full of settings doesn't need a title and a paragraph per row)
// and its control on the right.
export function SettingsRow({ label, tooltip = null, children }) {
    return (
        <div className="mt-2 mb-2 d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center position-relative">
                {label}
                {tooltip && <SettingsInfoTooltip tooltip={tooltip} />}
            </div>
            <div className="ml-2" />
            {children}
        </div>
    );
}

// Optional provenance shown only when the API supplies own-settings
// metadata. Resetting posts null, which removes the location override and
// immediately returns the field to the organization's effective value.
export function SettingInheritanceStatus({
    ownSettings,
    field,
    organizationName,
    onReset,
    // Which edge the caption hangs from. It follows the right edge by default,
    // which is where a lone control ends; when a row holds several controls
    // side by side each caption starts at its own control instead, or they all
    // pile up on the right and none says which field it describes.
    align = "end",
}) {
    const { t } = useTranslation();
    if (ownSettings === null || ownSettings === undefined) return null;

    const isOwn = Object.prototype.hasOwnProperty.call(ownSettings, field);
    // The caption goes under the control, not over it: over it, a caption that
    // wrapped (the own one is two labels long) pushed its control down and the
    // controls sharing the row no longer lined up.
    return (
        <div
            className={`d-flex align-items-center flex-wrap mt-1 justify-content-${align}`}
        >
            <small className={isOwn ? "text-secondary" : "text-info"}>
                {isOwn
                    ? t("mod_pms_location_customization_from_location")
                    : t("mod_pms_location_customization_from_organization", {
                          name: organizationName || t("mod_organizations_title"),
                      })}
            </small>
            {isOwn && (
                <div className="ml-2">
                    <RemoveAction
                        label={t("mod_organizations_location_settings_inherit")}
                        isVisible
                        onClick={onReset}
                    />
                </div>
            )}
        </div>
    );
}

export function InlineValidatedTextInput({
    inputTestID,
    saveButtonTestID,

    handleSave,
    initialValue,

    required,
    placeholder,
    maxLength,
    className = "",
    inputClassName = "",
} = {}) {

    //LOCAL STATE
    const [text, setText] = useState(initialValue);
    const [isValid, setIsValid] = useState(false);
    const [isEdited, setIsEdited] = useState(false);

    //OVERRIDING

    useEffect(() => {
        setIsEdited(text !== initialValue);
    }, [text, initialValue]);

    //ACTION HADLERS
    function handleDiscardChanges() {
        setText(initialValue);
    }

    function handleSaveChanges() {
        handleSave(text);
    }

    return (
        <div className={`d-flex align-items-center ${className}`}>
            {isEdited &&
                <>
                    <InlineEditButtons
                        acceptButtonTestID={saveButtonTestID}
                        isEnabled={isValid}
                        handleDiscardChanges={handleDiscardChanges}
                        handleConfirmChanges={handleSaveChanges}
                    />
                    <div className="mr-2" />
                </>
            }
            <ValidatedTextInput
                className={inputClassName}
                isRequired={required}
                isEditable

                placeholder={placeholder}
                testID={inputTestID}
                value={text}
                setValue={maxLength ? (v) => setText(v.slice(0, maxLength)) : setText}
                setIsValid={setIsValid}
            />
        </div>
    );
}
